import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { CSVFile, CSVRow, processCSV } from "@redotech/csv/csv-parser";
import { Currency } from "@redotech/money/currencies";
import { OrderImportField } from "@redotech/redo-fulfillment-common/import-schema";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { CreateFulfillmentOrder } from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { ProductInfo } from "@redotech/redo-product-common/product";
import {
  collectParseResults,
  FieldToColumnMap,
  ParseResult,
} from "./validate-fulfillment-orders";

// CSV SCHEMA (one row per line item, grouped by Order #):
// Order #, Order Date, Date Paid, Order Total, Amount Paid, Tax, Shipping Paid,
// Shipping Service, Height(in), Length(in), Width(in), Weight(oz),
// Custom Field 1, Custom Field 2, Custom Field 3, Order Source,
// Notes to the Buyer, Notes from the Buyer, Internal Notes, Gift Message, Gift Flag,
// Buyer Full Name, Buyer First Name, Buyer Last Name, Buyer Email, Buyer Phone, Buyer Username,
// Recipient Full Name, Recipient First Name, Recipient Last Name, Recipient Phone, Recipient Company,
// Address Line 1, Address Line 2, Address Line 3, City, State, Postal Code, Country Code,
// Item SKU, Item Name / Title, Item Quantity, Item Unit Price, Item Weight (oz),
// Item Options, Item Warehouse Location, Item Marketplace ID

export enum AdultsAndCraftsCsvColumns {
  ORDER_NUMBER = "Order #",
  ORDER_DATE = "Order Date",
  DATE_PAID = "Date Paid",
  BUYER_EMAIL = "Buyer Email",
  BUYER_PHONE = "Buyer Phone",
  RECIPIENT_FULL_NAME = "Recipient Full Name",
  RECIPIENT_FIRST_NAME = "Recipient First Name",
  RECIPIENT_LAST_NAME = "Recipient Last Name",
  RECIPIENT_COMPANY = "Recipient Company",
  ADDRESS_1 = "Address Line 1",
  ADDRESS_2 = "Address Line 2",
  CITY = "City",
  STATE = "State",
  POSTAL_CODE = "Postal Code",
  COUNTRY_CODE = "Country Code",
  ITEM_SKU = "Item SKU",
  ITEM_NAME = "Item Name / Title",
  ITEM_QUANTITY = "Item Quantity",
  UNIT_PRICE = "Item Unit Price",
  ITEM_WEIGHT = "Item Weight (oz)",
  INTERNAL_NOTES = "Internal Notes",
  NOTES_TO_BUYER = "Notes to the Buyer",
  GIFT_MESSAGE = "Gift Message",
}

export const adultsAndCraftsColumnMap: FieldToColumnMap = {
  [OrderImportField.ORDER_NUMBER]: AdultsAndCraftsCsvColumns.ORDER_NUMBER,
  [OrderImportField.ORDER_DATE]: AdultsAndCraftsCsvColumns.ORDER_DATE,
  [OrderImportField.DATE_PAID]: AdultsAndCraftsCsvColumns.DATE_PAID,
  [OrderImportField.INTERNAL_NOTES]: AdultsAndCraftsCsvColumns.INTERNAL_NOTES,
  [OrderImportField.RECIPIENT_FULL_NAME]:
    AdultsAndCraftsCsvColumns.RECIPIENT_FULL_NAME,
  [OrderImportField.ADDRESS_LINE_1]: AdultsAndCraftsCsvColumns.ADDRESS_1,
  [OrderImportField.ADDRESS_LINE_2]: AdultsAndCraftsCsvColumns.ADDRESS_2,
  [OrderImportField.CITY]: AdultsAndCraftsCsvColumns.CITY,
  [OrderImportField.STATE]: AdultsAndCraftsCsvColumns.STATE,
  [OrderImportField.POSTAL_CODE]: AdultsAndCraftsCsvColumns.POSTAL_CODE,
  [OrderImportField.BUYER_EMAIL]: AdultsAndCraftsCsvColumns.BUYER_EMAIL,
  [OrderImportField.BUYER_PHONE]: AdultsAndCraftsCsvColumns.BUYER_PHONE,
  [OrderImportField.ITEM_SKU]: AdultsAndCraftsCsvColumns.ITEM_SKU,
  [OrderImportField.ITEM_NAME]: AdultsAndCraftsCsvColumns.ITEM_NAME,
  [OrderImportField.ITEM_QUANTITY]: AdultsAndCraftsCsvColumns.ITEM_QUANTITY,
  [OrderImportField.ITEM_UNIT_PRICE]: AdultsAndCraftsCsvColumns.UNIT_PRICE,
  [OrderImportField.ITEM_WEIGHT_OZ]: AdultsAndCraftsCsvColumns.ITEM_WEIGHT,
  [OrderImportField.NOTES_TO_BUYER]: AdultsAndCraftsCsvColumns.NOTES_TO_BUYER,
  [OrderImportField.GIFT_MESSAGE]: AdultsAndCraftsCsvColumns.GIFT_MESSAGE,
};

export async function parseAdultsAndCraftsCsv(
  file: File,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<ParseResult> {
  const data: CSVFile = await processCSV(file);

  // Group rows by order number (one row per line item in this format)
  const orderGroups = new Map<string, CSVRow[]>();
  for (const row of data.rows) {
    const orderNumber = row[AdultsAndCraftsCsvColumns.ORDER_NUMBER] ?? "";
    if (!orderNumber) {
      continue;
    }
    const existing = orderGroups.get(orderNumber) ?? [];
    existing.push(row);
    orderGroups.set(orderNumber, existing);
  }

  const entries = Array.from(orderGroups.entries());

  return collectParseResults(
    entries,
    async ([, rows]) => buildOrder(rows, searchProducts),
    ([orderNumber]) => orderNumber,
  );
}

async function buildOrder(
  rows: CSVRow[],
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<CreateFulfillmentOrder> {
  const firstRow = rows[0];
  if (!firstRow) {
    throw new Error("Failed to parse order - no rows found");
  }

  const recipientFullName =
    firstRow[AdultsAndCraftsCsvColumns.RECIPIENT_FULL_NAME] ?? "";
  const firstName =
    firstRow[AdultsAndCraftsCsvColumns.RECIPIENT_FIRST_NAME] ?? "";
  const lastName =
    firstRow[AdultsAndCraftsCsvColumns.RECIPIENT_LAST_NAME] ?? "";
  const name = recipientFullName || `${firstName} ${lastName}`.trim();

  const email = firstRow[AdultsAndCraftsCsvColumns.BUYER_EMAIL] ?? "";
  const phone = firstRow[AdultsAndCraftsCsvColumns.BUYER_PHONE] ?? "";
  const address1 = firstRow[AdultsAndCraftsCsvColumns.ADDRESS_1] ?? "";
  const address2 = firstRow[AdultsAndCraftsCsvColumns.ADDRESS_2] ?? "";
  const city = firstRow[AdultsAndCraftsCsvColumns.CITY] ?? "";
  const state = firstRow[AdultsAndCraftsCsvColumns.STATE] ?? "";
  const zip = firstRow[AdultsAndCraftsCsvColumns.POSTAL_CODE] ?? "";
  const orderNumber = firstRow[AdultsAndCraftsCsvColumns.ORDER_NUMBER] ?? "";
  const internalNotes =
    firstRow[AdultsAndCraftsCsvColumns.INTERNAL_NOTES] ?? "";
  const giftMessage = firstRow[AdultsAndCraftsCsvColumns.GIFT_MESSAGE] ?? "";
  const noteToBuyer = firstRow[AdultsAndCraftsCsvColumns.NOTES_TO_BUYER] ?? "";
  const orderDateStr = firstRow[AdultsAndCraftsCsvColumns.ORDER_DATE] ?? "";
  const paidDateStr = firstRow[AdultsAndCraftsCsvColumns.DATE_PAID] ?? "";
  const orderDate = orderDateStr ? new Date(orderDateStr) : new Date();
  const paidDate = paidDateStr ? new Date(paidDateStr) : new Date();

  if (!name || !address1 || !city || !state || !zip) {
    throw new Error("Failed to parse row - missing required address fields");
  }

  const items: FulfillmentOrderLineItem[] = (
    await Promise.all(
      rows.map(async (row) => {
        const sku = row[AdultsAndCraftsCsvColumns.ITEM_SKU]?.trim() ?? "";
        const itemName = row[AdultsAndCraftsCsvColumns.ITEM_NAME]?.trim() ?? "";
        const quantityStr =
          row[AdultsAndCraftsCsvColumns.ITEM_QUANTITY]?.trim() ?? "";
        const unitPriceStr =
          row[AdultsAndCraftsCsvColumns.UNIT_PRICE]?.trim() ?? "0";
        const weightStr =
          row[AdultsAndCraftsCsvColumns.ITEM_WEIGHT]?.trim() ?? "0";

        if (!sku && !itemName) {
          return null;
        }

        const quantity = parseInt(quantityStr, 10);
        if (isNaN(quantity) || quantity <= 0) {
          return null;
        }
        if (quantity > 1000) {
          throw new Error("Failed to parse row - quantity too high");
        }

        const weightOz = parseFloat(weightStr) || 0;
        const weightGrams = Math.round(weightOz * 28.3495);

        let populatedItem: ReturnType<typeof getPopulatedItemFromSku> = {
          variant: undefined,
          variants: [],
        };
        if (sku) {
          const { products } = await searchProducts(sku);
          populatedItem = getPopulatedItemFromSku(sku, products);
        }

        const rawPrice = populatedItem?.variant?.price ?? unitPriceStr;
        const unitPrice = isNaN(parseFloat(rawPrice)) ? "0" : rawPrice;
        const totalPrice = (parseFloat(unitPrice) * quantity).toString();

        const lineItem: FulfillmentOrderLineItem = {
          sku,
          quantity,
          unitPrice: { amount: unitPrice, currency: Currency.USD },
          totalPrice: { amount: totalPrice, currency: Currency.USD },
          imageUrl: populatedItem?.image?.url,
          variantId: populatedItem?.variant?.id ?? sku,
          productId: populatedItem?.id ?? sku,
          vendor: "Adults & Crafts",
          requiresShipping: true,
          externalId: sku,
          customAttributes: [],
          totalQuantity: quantity,
          unfulfilledQuantity: quantity,
          weight: {
            unit: WeightUnit.G,
            value: populatedItem?.variant?.weightGrams ?? weightGrams,
          },
          lineItemId: sku,
          title: populatedItem?.name ?? itemName,
          productTitle: populatedItem?.name ?? itemName,
          variantTitle: populatedItem?.variant?.name ?? itemName,
          unitDiscountedPrice: { amount: unitPrice, currency: Currency.USD },
          totalDiscountedPrice: { amount: totalPrice, currency: Currency.USD },
          totalTax: { amount: "0", currency: Currency.USD },
          hsCode: null,
        };

        return lineItem;
      }),
    )
  ).filter((item): item is FulfillmentOrderLineItem => item !== null);

  const orderData: CreateFulfillmentOrder = {
    address: {
      countryCode: RedoShopifyCountryCode.Us,
      name,
      email,
      address1,
      address2,
      city,
      province: state,
      zip,
      phone,
    },
    orderInfo: {
      orderNumber,
      orderDate,
      paidDate,
      shippingPaid: { amount: "0", currency: Currency.USD },
      taxPaid: { amount: "0", currency: Currency.USD },
      totalPaid: { amount: "0", currency: Currency.USD },
      items,
      internalNote: internalNotes,
      noteToBuyer: giftMessage || noteToBuyer,
    },
  };

  return orderData;
}

const getPopulatedItemFromSku = (sku: string, products: ProductInfo[]) => {
  const matchedProduct = products.find((product) =>
    product.variants.some((variant) => variant.sku === sku),
  );
  const matchedVariant = matchedProduct?.variants.find(
    (variant) => variant.sku === sku,
  );
  return { ...matchedProduct, variant: matchedVariant };
};
